import React from "react";
import { Icon } from "../Icon";

const HEIGHT = { sm: 32, md: 36 };

/**
 * Input estilizado con tokens DS.
 * Soporta `icon` izquierdo (Lucide name) y se comporta como un <input> normal:
 * recibe `value`/`onChange`/`type`/`name`/`placeholder`/`disabled`/`required`/`autoFocus`.
 */
export function Input({
  value,
  onChange,
  placeholder,
  icon,
  type = "text",
  name,
  id,
  disabled = false,
  required = false,
  size = "md",
  autoFocus = false,
  step,
  min,
  max,
  maxLength,
  rows,
  style,
  inputStyle,
  inputProps,
}) {
  const Tag = type === "textarea" ? "textarea" : "input";
  const minHeight = type === "textarea" ? undefined : HEIGHT[size];
  const padding = type === "textarea" ? "8px 12px" : "0 12px";
  return (
    <div
      style={{
        minHeight,
        border: "1px solid var(--kr-border)",
        background: disabled ? "var(--kr-bg-muted)" : "var(--kr-bg-surface)",
        borderRadius: 6,
        padding,
        display: "flex",
        alignItems: type === "textarea" ? "stretch" : "center",
        gap: 8,
        transition: "border-color var(--kr-dur-fast) var(--kr-ease-out), box-shadow var(--kr-dur-fast) var(--kr-ease-out)",
        ...(style || {}),
      }}
    >
      {icon && type !== "textarea" && (
        <Icon name={icon} size={14} style={{ color: "var(--kr-fg-muted)", flexShrink: 0 }} />
      )}
      <Tag
        id={id}
        name={name}
        type={type === "textarea" ? undefined : type}
        rows={type === "textarea" ? (rows || 3) : undefined}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        step={step}
        min={min}
        max={max}
        maxLength={maxLength}
        {...(inputProps || {})}
        style={{
          flex: 1,
          border: "none",
          outline: "none",
          background: "transparent",
          font: "400 13px/1.4 var(--kr-font-sans)",
          color: "var(--kr-fg-strong)",
          width: "100%",
          resize: type === "textarea" ? "vertical" : "none",
          ...(inputStyle || {}),
        }}
      />
    </div>
  );
}

/**
 * Select con look de Input.
 */
export function Select({
  value,
  onChange,
  name,
  id,
  disabled = false,
  required = false,
  children,
  size = "md",
  style,
}) {
  return (
    <div
      style={{
        height: HEIGHT[size],
        border: "1px solid var(--kr-border)",
        background: disabled ? "var(--kr-bg-muted)" : "var(--kr-bg-surface)",
        borderRadius: 6,
        padding: "0 12px",
        display: "flex",
        alignItems: "center",
        gap: 8,
        ...(style || {}),
      }}
    >
      <select
        id={id}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        disabled={disabled}
        required={required}
        style={{
          flex: 1,
          border: "none",
          outline: "none",
          background: "transparent",
          font: "400 13px/1.4 var(--kr-font-sans)",
          color: "var(--kr-fg-strong)",
          appearance: "none",
          WebkitAppearance: "none",
        }}
      >
        {children}
      </select>
      <span
        aria-hidden="true"
        style={{
          color: "var(--kr-fg-muted)",
          fontSize: 12,
          pointerEvents: "none",
          marginLeft: -4,
        }}
      >
        ▾
      </span>
    </div>
  );
}

export default Input;
